import React, { useState } from 'react';
import { X, ExternalLink, ShieldCheck, Lock, Copy, Check, Smartphone, Monitor } from 'lucide-react';
import { sanitizePaymentUrl, safeWindowOpen } from '../lib/mobilePaymentUtils';

interface EmbeddedPaymentIframeModalProps {
  isOpen: boolean;
  onClose: () => void;
  checkoutUrl: string | null;
  gatewayName?: string;
  amountBrl?: number;
  title?: string;
  orderId?: string;
  onPaymentComplete?: () => void;
}

export const EmbeddedPaymentIframeModal: React.FC<EmbeddedPaymentIframeModalProps> = ({
  isOpen,
  onClose,
  checkoutUrl,
  gatewayName = 'InfinitePay',
  amountBrl,
  title = 'Finalizar Pagamento Seguro',
  orderId,
  onPaymentComplete
}) => {
  const [copied, setCopied] = useState(false);
  const [viewMode, setViewMode] = useState<'fit' | 'mobile'>('fit');

  const cleanUrl = checkoutUrl ? sanitizePaymentUrl(checkoutUrl) : '';

  if (!isOpen || !cleanUrl) return null;

  const handleOpenExternal = () => {
    safeWindowOpen(cleanUrl);
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(cleanUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const cleanGatewayName = (gatewayName || '').toLowerCase();
  const isInfinitePay = cleanGatewayName.includes('infinite');
  const isMercadoPago = cleanGatewayName.includes('mercado');
  const isStripe = cleanGatewayName.includes('stripe');
  const isPayPal = cleanGatewayName.includes('paypal');

  const themeColors = isInfinitePay
    ? {
        border: 'border-emerald-500/40',
        badgeBg: 'bg-emerald-500/10 text-emerald-300 border-emerald-500/30',
        gradientHeader: 'from-emerald-950 via-slate-900 to-slate-950',
        accentColor: 'text-emerald-400',
        btnBg: 'bg-emerald-600 hover:bg-emerald-500 text-white'
      }
    : isMercadoPago
    ? {
        border: 'border-sky-500/40',
        badgeBg: 'bg-sky-500/10 text-sky-300 border-sky-500/30',
        gradientHeader: 'from-sky-950 via-slate-900 to-slate-950',
        accentColor: 'text-sky-400',
        btnBg: 'bg-sky-600 hover:bg-sky-500 text-white'
      }
    : {
        border: 'border-slate-700',
        badgeBg: 'bg-slate-800 text-slate-300 border-slate-700',
        gradientHeader: 'from-slate-900 to-slate-950',
        accentColor: 'text-emerald-400',
        btnBg: 'bg-slate-800 hover:bg-slate-700 text-white'
      };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4 md:p-6 bg-slate-950/85 backdrop-blur-md animate-fade-in">
      <div 
        className={`bg-slate-900 border ${themeColors.border} text-white rounded-3xl w-full ${
          viewMode === 'mobile' ? 'max-w-md' : 'max-w-4xl'
        } h-[92vh] max-h-[860px] flex flex-col shadow-2xl overflow-hidden transition-all duration-300`}
      >
        {/* Modal Header */}
        <div className={`px-4 sm:px-6 py-3.5 bg-gradient-to-r ${themeColors.gradientHeader} border-b border-slate-800 flex items-center justify-between gap-3 shrink-0`}>
          <div className="flex items-center gap-3 min-w-0">
            <div className="p-2 rounded-xl bg-slate-900/80 border border-slate-700/60 shadow-sm shrink-0">
              <Lock className={`w-4 h-4 ${themeColors.accentColor}`} />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-extrabold text-sm sm:text-base text-white truncate">
                  {gatewayName} Checkout
                </span>
                <span className={`text-[10px] font-black uppercase px-2 py-0.5 rounded-full border ${themeColors.badgeBg} shrink-0 hidden xs:inline-flex items-center gap-1`}>
                  <ShieldCheck className="w-3 h-3" />
                  <span>Janela Segura</span>
                </span>
              </div>
              <p className="text-[11px] text-slate-400 truncate">
                {title} {orderId ? `• Ref: #${orderId}` : ''}
              </p>
            </div>
          </div>

          {/* Right Header Actions */}
          <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
            {amountBrl !== undefined && amountBrl > 0 && (
              <div className="hidden sm:block text-right px-3 py-1 bg-slate-950/60 rounded-xl border border-slate-800 mr-1">
                <span className="text-[10px] text-slate-400 block uppercase tracking-wider font-bold">Total</span>
                <span className={`text-xs sm:text-sm font-black ${themeColors.accentColor}`}>
                  R$ {amountBrl.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
            )}

            {/* View Mode Toggle (Mobile / Expanded) */}
            <div className="hidden md:flex items-center bg-slate-950/80 border border-slate-800 rounded-xl p-0.5">
              <button
                type="button"
                onClick={() => setViewMode('fit')}
                title="Visualização Ampla"
                className={`p-1.5 rounded-lg text-xs transition-colors cursor-pointer ${
                  viewMode === 'fit' ? 'bg-slate-800 text-white' : 'text-slate-400 hover:text-slate-200'
                }`}
              >
                <Monitor className="w-3.5 h-3.5" />
              </button>
              <button
                type="button"
                onClick={() => setViewMode('mobile')}
                title="Visualização Celular"
                className={`p-1.5 rounded-lg text-xs transition-colors cursor-pointer ${
                  viewMode === 'mobile' ? 'bg-slate-800 text-white' : 'text-slate-400 hover:text-slate-200'
                }`}
              >
                <Smartphone className="w-3.5 h-3.5" />
              </button>
            </div>

            {/* Open in New Window (Fallback option) */}
            <button
              type="button"
              onClick={handleOpenExternal}
              title="Abrir em nova aba do navegador"
              className="p-2 text-slate-400 hover:text-white hover:bg-slate-800/80 rounded-xl transition-colors cursor-pointer"
            >
              <ExternalLink className="w-4 h-4" />
            </button>

            {/* Close Modal */}
            <button
              type="button"
              onClick={onClose}
              className="p-2 text-slate-400 hover:text-rose-400 hover:bg-rose-500/10 rounded-xl transition-colors cursor-pointer"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Amount bar for small screens */}
        {amountBrl !== undefined && amountBrl > 0 && (
          <div className="sm:hidden px-4 py-2 bg-slate-950 border-b border-slate-800 flex items-center justify-between text-xs">
            <span className="text-slate-400 font-medium">Valor do pagamento:</span>
            <span className={`font-black ${themeColors.accentColor}`}>
              R$ {amountBrl.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          </div>
        )}

        {/* Main Content Area - Replaced iFrame with Secure Popup Launcher */}
        <div className="relative flex-1 bg-slate-950 w-full overflow-hidden flex flex-col items-center justify-center p-6 text-center">
          
          <div className="max-w-md w-full space-y-8">
            <div className="relative mx-auto w-24 h-24 flex items-center justify-center">
              <div className={`absolute inset-0 rounded-full blur-xl opacity-20 ${isMercadoPago ? 'bg-sky-500' : 'bg-emerald-500'} animate-pulse`} />
              <div className={`relative w-20 h-20 rounded-full border border-slate-700 bg-slate-900 flex items-center justify-center shadow-2xl`}>
                <Lock className={`w-10 h-10 ${themeColors.accentColor}`} />
              </div>
            </div>

            <div className="space-y-3">
              <h3 className="text-xl sm:text-2xl font-black text-white">
                Ambiente Seguro de Pagamento
              </h3>
              <p className="text-sm text-slate-400 leading-relaxed">
                Para garantir a máxima segurança dos seus dados financeiros, o {gatewayName} exige que o pagamento seja finalizado em uma janela oficial criptografada.
              </p>
            </div>

            <div className="pt-4 pb-2">
              <button
                type="button"
                onClick={() => safeWindowOpen(cleanUrl, 'payment_window_secure')}
                className={`w-full py-4 px-6 rounded-2xl font-black text-sm sm:text-base shadow-lg transition-all transform active:scale-95 flex items-center justify-center gap-3 ${themeColors.btnBg} hover:shadow-${isMercadoPago ? 'sky' : 'emerald'}-500/20`}
              >
                <ExternalLink className="w-5 h-5" />
                <span>Abrir Janela Segura de Pagamento</span>
              </button>
            </div>

            <div className="p-4 bg-slate-900/60 rounded-xl border border-slate-800 text-left flex items-start gap-3">
              <ShieldCheck className={`w-5 h-5 mt-0.5 ${themeColors.accentColor} shrink-0`} />
              <div className="space-y-1">
                <p className="text-xs font-bold text-slate-200">Não feche esta tela ainda.</p>
                <p className="text-[11px] text-slate-400">
                  Após concluir o pagamento na nova janela, volte aqui e clique em "Concluir" no rodapé para confirmarmos seu pedido.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Modal Footer with Support, Link Copy, and External Option */}
        <div className="px-4 sm:px-6 py-3 bg-slate-950 border-t border-slate-800 flex flex-col sm:flex-row items-center justify-between gap-2.5 text-xs shrink-0">
          <div className="flex items-center gap-2 text-slate-400 text-[11px] text-center sm:text-left">
            <ShieldCheck className="w-4 h-4 text-emerald-400 shrink-0" />
            <span>
              Você permanece no site <strong>Do Japão Pra Sua Casa</strong> durante todo o processo.
            </span>
          </div>

          <div className="flex items-center gap-2 w-full sm:w-auto justify-end">
            <button
              type="button"
              onClick={handleCopyLink}
              className="py-1.5 px-3 bg-slate-800 hover:bg-slate-700 text-slate-300 text-[11px] font-bold rounded-xl transition-colors flex items-center gap-1.5 cursor-pointer"
            >
              {copied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
              <span>{copied ? 'Copiado!' : 'Copiar Link'}</span>
            </button>

            <button
              type="button"
              onClick={handleOpenExternal}
              className="py-1.5 px-3 bg-slate-800 hover:bg-slate-700 text-slate-200 text-[11px] font-bold rounded-xl transition-colors flex items-center gap-1.5 cursor-pointer"
            >
              <ExternalLink className="w-3.5 h-3.5" />
              <span>Abrir em Nova Aba</span>
            </button>

            <button
              type="button"
              onClick={() => {
                if (onPaymentComplete) onPaymentComplete();
                onClose();
              }}
              className="py-1.5 px-4 bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-[11px] rounded-xl transition-all shadow-md shadow-emerald-900/20 flex items-center gap-1 cursor-pointer"
            >
              <Check className="w-3.5 h-3.5 stroke-[3]" />
              <span>Concluir</span>
            </button>
          </div>
        </div>

      </div>
    </div>
  );
};

export default EmbeddedPaymentIframeModal;
